import * as R from 'ramda'
import * as React from 'react'
import { useMemo } from 'react'

import {
  formatInTimeZone,
  formatZonedToUTC,
  useBusinessRules,
  useContent,
  useQuery,
} from '@ngb-frontend/shared/context'
import {
  Feature,
  type DateFormValues,
  type DateStepPrerequisiteData,
  type DateStepValues,
  type StepProps,
} from '@ngb-frontend/shared/types'
import { setDateTime, splitDateTime } from '@ngb-frontend/shared/utils'

import { DateForm } from '../../forms/DateForm/DateForm'

const DateStep: React.FC<
  StepProps<DateStepValues, DateStepPrerequisiteData>
> = ({ onNextStep, onUpdateStep, stepData, booking, mergedData }) => {
  const c = useContent()
  const query = useQuery()
  const rules = useBusinessRules()
  const dateRules = rules.dates(Feature.MnR)
  const hasPickup = mergedData.extraServices?.includes('PickupAndReturn')
  const fixedPickupTime = hasPickup ? dateRules.pickup.fixedTime : undefined

  const initialValuesRef = React.useRef<DateFormValues | undefined>(
    hasDateFormValues(stepData)
      ? {
          ...splitDateTime(
            formatInTimeZone(stepData.dateTime, {
              country: query?.country,
            }),
          ),
          alternativeDates: stepData.alternativeDates,
        }
      : booking
      ? splitDateTime(
          formatInTimeZone(booking.service_date_time || '', {
            country: query?.country,
          }),
        )
      : undefined,
  )

  const minDate = dateRules.getMinDate()
  const disabledDates = dateRules.disabledDates

  /**
   * We manipulate time in the following manner
   * 1) Create a date object (always system timezone) and set date and hours based on form values
   *    This means that for UK in a Dutch browser, we use the Dutch TimeZone but this is
   *    not problematic since we store the date ISO string only after (2)
   * 2) Convert the date object to a UTC ISO string to store in the state of the app / send to BE
   * 3) Whenever we read a date from state, we format it (see getZonedDateTime)
   *    through the "formatInTimeZone" function, which formats a date in TimeZone
   *    regardless of the system timezone
   */
  const handleSubmit = React.useCallback(
    (values: DateFormValues) => {
      const localDateTime = setDateTime(values.date, values.time)
      const utcDateTime = formatZonedToUTC(localDateTime, {
        country: query?.country,
      })

      onUpdateStep({
        dateTime: utcDateTime.toISOString(),
        alternativeDates: values.alternativeDates,
      })
      onNextStep()
    },
    [onNextStep, onUpdateStep, query?.country],
  )

  const timeConfig = useMemo(() => {
    const mergedServices = [
      ...(mergedData.services || []),
      ...(mergedData.extraServices || []),
    ]

    return rules.operatingHours(mergedServices)
  }, [mergedData.extraServices, mergedData.services, rules])

  return (
    <DateForm
      fixedTime={fixedPickupTime}
      hint={fixedPickupTime ? c('fields.date.hint') : ''}
      timeConfig={timeConfig}
      onSubmit={handleSubmit}
      initialValues={initialValuesRef.current}
      minDate={minDate}
      disabledDates={disabledDates}
    />
  )
}

export const hasDateFormValues = (x: unknown): x is DateStepValues =>
  R.allPass([R.pipe(R.prop('dateTime'), R.is(String))])(x)

export default DateStep
